/**
 * Banker Portal — Login.
 * Bankers are NOT in the staff `users` collection — they live in
 * `participating_bankers` and authenticate via their own JWT issued by
 * `/api/banker/auth/login`. Token stored under `banker_token`.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Landmark, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function BankerLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/api/banker/auth/login`, { email, password });
      localStorage.setItem('banker_token', r.data.token);
      localStorage.setItem('banker_profile', JSON.stringify(r.data.banker));
      toast.success(`Welcome, ${r.data.banker.name}`);
      navigate('/banker/inbox');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="banker-login-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-indigo-100 text-indigo-700">
            <Landmark className="w-7 h-7" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Banker Portal</h1>
          <p className="text-sm text-gray-500">Sign in to review loan cases routed to your bank.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              data-testid="banker-login-email"
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              data-testid="banker-login-password"
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full" data-testid="banker-login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
          </Button>
        </form>
        <p className="text-xs text-center text-gray-500">
          First time?{' '}
          <Link to="/banker/setup" className="text-indigo-600 hover:underline">
            Activate with invite token
          </Link>
        </p>
      </div>
    </div>
  );
}
